import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  resetPasswordForm!: FormGroup;
  isLoading = false;
  resetSuccess = false;
  showPassword = false;
  showConfirmPassword = false;
  passwordStrength = 0;
  errorMessage = '';
  token: string = '';
  uid: string = '';
  isValidToken = true;
  isCheckingToken = true;

  passwordRequirements = [
    { label: 'Au moins 8 caractères', met: false, icon: 'bi-check-circle-fill' },
    { label: 'Une lettre majuscule', met: false, icon: 'bi-check-circle-fill' },
    { label: 'Une lettre minuscule', met: false, icon: 'bi-check-circle-fill' },
    { label: 'Un chiffre', met: false, icon: 'bi-check-circle-fill' },
    { label: 'Un caractère spécial', met: false, icon: 'bi-check-circle-fill' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.validateToken();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  validateToken(): void {
    // Le backend (Djoser) n'expose pas de endpoint de pre-validation du
    // couple uid/token : la seule verification reelle se fait au moment de
    // la soumission (reset_password_confirm). On se contente donc ici de
    // verifier que l'URL contient bien les deux parametres necessaires,
    // sans simuler une validation serveur qui n'existe pas.
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.token = params['token'] || '';
        this.uid = params['uid'] || '';
        this.isValidToken = !!(this.token && this.uid);
        this.isCheckingToken = false;

        if (!this.isValidToken) {
          this.errorMessage = 'Le lien de réinitialisation est invalide ou a expiré';
        }
      });
  }

  initForm(): void {
    this.resetPasswordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });

    // Watch password changes
    this.resetPasswordForm.get('password')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(password => {
        this.passwordStrength = this.calculatePasswordStrength(password);
        this.updatePasswordRequirements(password);
      });
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  calculatePasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;

    return Math.min(strength, 100);
  }

  updatePasswordRequirements(password: string): void {
    this.passwordRequirements[0].met = password.length >= 8;
    this.passwordRequirements[1].met = /[A-Z]/.test(password);
    this.passwordRequirements[2].met = /[a-z]/.test(password);
    this.passwordRequirements[3].met = /[0-9]/.test(password);
    this.passwordRequirements[4].met = /[^a-zA-Z0-9]/.test(password);
  }

  getPasswordStrengthLabel(): string {
    if (this.passwordStrength < 25) return 'Très faible';
    if (this.passwordStrength < 50) return 'Faible';
    if (this.passwordStrength < 75) return 'Moyen';
    if (this.passwordStrength < 90) return 'Bon';
    return 'Excellent';
  }

  getPasswordStrengthClass(): string {
    if (this.passwordStrength < 25) return 'very-weak';
    if (this.passwordStrength < 50) return 'weak';
    if (this.passwordStrength < 75) return 'medium';
    if (this.passwordStrength < 90) return 'good';
    return 'strong';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (this.resetPasswordForm.valid && this.isValidToken) {
      this.isLoading = true;
      this.errorMessage = '';

      const data = {
        token: this.token,
        uid: this.uid,
        new_password: this.resetPasswordForm.value.password,
      };

      this.authService.resetPassword(data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.resetSuccess = true;
          setTimeout(() => {
            this.navigateToSignin();
          }, 3000);
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.errorMessage = this.extractErrorMessage(error);

          // Un token/uid invalide ou expire renvoye par le backend a ce
          // stade doit ramener l'utilisateur vers l'ecran "lien invalide".
          const fieldErrors = error?.error || {};
          if (fieldErrors.token || fieldErrors.uid) {
            this.isValidToken = false;
          }
        },
      });

    } else {
      Object.keys(this.resetPasswordForm.controls).forEach(key => {
        this.resetPasswordForm.get(key)?.markAsTouched();
      });
    }
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const body = error?.error;

    if (!body) {
      return 'Une erreur est survenue. Veuillez reessayer.';
    }

    if (typeof body === 'string') {
      return body;
    }

    if (body.message) {
      return body.message;
    }

    if (body.detail) {
      return body.detail;
    }

    // Reponses DRF/Djoser: { "token": ["..."], "uid": ["..."], "new_password": ["..."] }
    for (const key of ['token', 'uid', 'new_password', 'non_field_errors']) {
      const value = body[key];
      if (Array.isArray(value) && value.length) {
        return value[0];
      }
      if (typeof value === 'string' && value) {
        return value;
      }
    }

    return 'Une erreur est survenue. Veuillez reessayer.';
  }

  navigateToSignin(): void {
    this.router.navigate(['/auth/signin']);
  }

  requestNewLink(): void {
    this.router.navigate(['/auth/forgot-password']);
  }

  getFieldError(fieldName: string): string {
    const field = this.resetPasswordForm.get(fieldName);

    if (field?.hasError('required')) {
      return 'Ce champ est requis';
    }
    if (field?.hasError('minlength')) {
      const minLength = field.errors?.['minlength'].requiredLength;
      return `Minimum ${minLength} caractères requis`;
    }

    if (fieldName === 'confirmPassword' && this.resetPasswordForm.hasError('passwordMismatch')) {
      return 'Les mots de passe ne correspondent pas';
    }

    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.resetPasswordForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  get allRequirementsMet(): boolean {
    return this.passwordRequirements.every(req => req.met);
  }
}
